<script>
import useSharedData from "@/use/SharedData";
import useSharedMethod from "@/use/SharedMethod";
import useTools from "@/use/Tools";
import {onMounted, ref} from "vue";
import axios from "axios";
import useValidation from "@/use/Validation";
import {useContratStore} from "@/stores/Contrat";
import StrQuillEditorCorePdf from "@/components/StrQuillEditorCorePdf.vue";

export default {
    components: {StrQuillEditorCorePdf},
    setup() {
        const {mxRules} = useValidation();
        const {$route, $refs, $moment} = useTools();
        const {loading, dialog} = useSharedData();
        const {getBack, formatedDate} = useSharedMethod();

        const contratStore = useContratStore();

        const contrat = ref({});
        const interimId = ref(null);
        const alreadyRenewed = ref(null);

        function findById(id) {
            loading.value = true;
            axios.get(`/contrats/${id}`).then(response => {
                contrat.value = response.data;

                if (contrat.value.trialPeriodStatus === 'RENEWED')
                    alreadyRenewed.value = true;

                contrat.value.trialPeriodStatus = 'VALIDATED';
            }).finally(() => {
                loading.value = false;
            })
        }

        function completeContratData() {
            axios.get(`/contrats/completeContratData`, {
                params: {
                    contratTemplateId: contrat.value.contratTemplateId,
                    start: $moment(contrat.value.trialPeriodEnd).add(1, "days").format("YYYY-MM-DD"),
                },
            }).then((response) => {
                const result = response.data;
                contrat.value.endRenewedTrialPeriod = result.trialPeriodEnd;
            });
        }

        function handleTrialPeriod() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put(`/contrats/handleTrialPeriod`, contrat.value, {
                    params: {
                        interimId: interimId.value,
                    },
                }).then((response) => {
                    if (contrat.value.trialPeriodStatus === 'REJECTED') {
                        contratStore.deleteContrat(response.data);
                    } else {
                        contratStore.updateContrat(response.data);
                    }
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        const html = ref(1);
        const stepper = ref(1);
        const firstStepIsValid = ref(true);
        const loadingImplementedModel = ref(false);

        function next() {
            firstStepIsValid.value = $refs.form.validate();
            if (firstStepIsValid.value) {
                getTrialPeriodImplementedModel();
            }
        }

        function getTrialPeriodImplementedModel() {
            if ($refs.form.validate()) {
                loadingImplementedModel.value = true;
                axios.put(`/contrats/getTrialPeriodImplementedModel`, contrat.value).then((response) => {
                    html.value = response.data;
                    stepper.value = 2;
                }).finally(() => {
                    loadingImplementedModel.value = false;
                });
            }
        }

        onMounted(() => findById($route.params.id));

        return {
            mxRules,
            contratStore,
            dialog,
            loading,
            stepper,
            firstStepIsValid,
            loadingImplementedModel,
            contrat,
            interimId,
            alreadyRenewed,
            html,
            completeContratData,
            handleTrialPeriod,
            formatedDate,
            next,
            getBack,
        }
    }
}
</script>

<template>
    <v-dialog
        v-model="dialog"
        max-width="800"
        scrollable
        persistent
    >
        <v-card>
            <w-toolbar color="teal" :title="$t('contrat.handle_trial_period')"/>
            <v-card-text>
                <v-stepper v-model="stepper" flat>
                    <v-stepper-header>
                        <v-stepper-step
                            :complete="stepper > 1"
                            step="1"
                        >
                            {{ $t('common.details') }}
                        </v-stepper-step>

                        <v-divider/>

                        <v-stepper-step
                            :complete="stepper > 2"
                            step="2"
                        >
                            Impression
                        </v-stepper-step>

                        <v-divider/>

                    </v-stepper-header>

                    <v-stepper-items>
                        <v-stepper-content step="1">
                            <v-form ref="form" lazy-validation>
                                <v-container fluid>
                                    <w-fieldset :title="$t('common.details')">
                                        <v-simple-table dense>
                                            <template v-slot:default>
                                                <tbody>
                                                <tr>
                                                    <td class="text-left">{{ $t('contrat.number') }}</td>
                                                    <td class="text-left">
                                                        <w-chip
                                                            :text="contrat.number"
                                                            :icon="contrat.workerHead ? 'mdi-account-supervisor-circle' : null"
                                                            icon-color="teal"
                                                        />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td class="text-left">{{ $t('module.worker') }}</td>
                                                    <td class="text-left">
                                                        <w-inline-text bold :text="contrat.workerName"/>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td class="text-left">{{ $t('common.modal') }}</td>
                                                    <td class="text-left">
                                                        <w-inline-text outlined :text="contrat.contratTemplateName"/>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td class="text-left">{{ $t('data_select.CONTRAT_NATURE') }}</td>
                                                    <td class="text-left">
                                                        <w-inline-text :text="contrat.natureName"/>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td class="text-left">{{ $t('common.start') }}</td>
                                                    <td class="text-left">
                                                        <w-inline-text :text="formatedDate(contrat.start)"/>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td class="text-left">{{ $t('common.end') }}</td>
                                                    <td class="text-left">
                                                        <w-chip color="teal" :text="formatedDate(contrat.end)"/>
                                                    </td>
                                                </tr>
                                                </tbody>
                                            </template>
                                        </v-simple-table>
                                    </w-fieldset>
                                    <br/>
                                    <w-fieldset title="Action">
                                        <v-row dense>
                                            <v-col cols="12">
                                                <v-radio-group
                                                    v-model="contrat.trialPeriodStatus"
                                                    :rules="[mxRules.required]"
                                                    row
                                                >
                                                    <v-radio
                                                        value="VALIDATED"
                                                        :label="$t('contrat.trial_period_status.VALIDATED')"
                                                        color="teal"
                                                    />
                                                    <v-radio
                                                        v-if="!alreadyRenewed"
                                                        value="RENEWED"
                                                        :label="$t('contrat.trial_period_status.RENEWED')"
                                                        color="cyan"
                                                        @click="completeContratData"
                                                    />
                                                    <v-radio
                                                        value="REJECTED"
                                                        :label="$t('contrat.trial_period_status.REJECTED')"
                                                        color="red"
                                                    />
                                                </v-radio-group>
                                            </v-col>
                                        </v-row>
                                    </w-fieldset>
                                    <br/>
                                    <w-fieldset
                                        v-if="contrat.trialPeriodStatus === 'VALIDATED'"
                                        :title="$t('common.comment')"
                                        color="teal"
                                    >
                                        <v-row dense>
                                            <v-col cols="12">
                                                <w-simple-text-area v-model="contrat.trialPeriodDescription"/>
                                            </v-col>
                                        </v-row>
                                    </w-fieldset>
                                    <w-fieldset
                                        v-else-if="contrat.trialPeriodStatus === 'RENEWED'"
                                        :title="$t('common.comment')"
                                        color="cyan"
                                    >
                                        <v-row dense>
                                            <v-col cols="12">
                                                <w-label :label="$t('contrat.end_renewed_trial_period')" required/>
                                                <w-date-picker
                                                    v-model="contrat.endRenewedTrialPeriod"
                                                    readonly
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <w-simple-text-area v-model="contrat.renewedTrialPeriodDescription"/>
                                            </v-col>
                                        </v-row>
                                    </w-fieldset>
                                    <w-fieldset
                                        v-else-if="contrat.trialPeriodStatus === 'REJECTED'"
                                        title="Résiliation"
                                        color="red"
                                    >
                                        <v-row dense>
                                            <v-col cols="6">
                                                <w-label :label="$t('data_select.CONTRAT_EXIT_RATIONALE')" required/>
                                                <w-data-select-autocomplete
                                                    v-model="contrat.exitRationaleId"
                                                    type="CONTRAT_EXIT_RATIONALE"
                                                    :rules="[mxRules.required]"
                                                />
                                            </v-col>
                                            <v-col cols="6">
                                                <w-label :label="$t('contrat.terminate_date')" required/>
                                                <w-date-picker
                                                    v-model="contrat.leavingDate"
                                                    :rules="[mxRules.required]"
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <w-label :label="$t('common.description')"/>
                                                <w-simple-text-area
                                                    v-model="contrat.leavingDescription"
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <w-label :label="$t('common.interim')" :required="contrat.workerHead"/>
                                                <str-worker-autocomplete
                                                    v-model="interimId"
                                                    :rules="contrat.workerHead ? [mxRules.required] : []"
                                                />
                                            </v-col>
                                        </v-row>
                                    </w-fieldset>
                                </v-container>
                            </v-form>
                        </v-stepper-content>

                        <v-stepper-content step="2">
                            <str-quill-editor-core-pdf v-model="html"/>
                        </v-stepper-content>


                    </v-stepper-items>
                </v-stepper>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-previous
                    :show="stepper === 2"
                    @press="stepper = 1"
                />
                <w-btn-next
                    :show="stepper === 1"
                    :error="!firstStepIsValid"
                    :loading="loadingImplementedModel"
                    @press="next"
                />
                <w-btn-print :show="stepper === 2"/>
                <w-btn-apply
                    :show="stepper === 2"
                    :loading="loading"
                    @press="handleTrialPeriod"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped>

</style>